import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Link,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { storeReturnsApi } from '../services/api';
import ReturnDecisionDialog from './ReturnDecisionDialog';
import LateShipmentsList from './LateShipmentsList';
import {
  ESCALATION_LABELS,
  RETURN_REASON_LABELS,
  RETURN_STATUS_LABELS,
  formatDateTime,
  formatMoney,
} from './storeReturnsUtils';

const PAGE_SIZE = 50;
const STATUS_FILTERS = [
  { value: 'ESCALATED', label: 'Escalated (needs a decision)' },
  { value: 'APPROVED', label: 'Approved (refund pending)' },
  { value: 'REFUNDED', label: 'Refunded' },
  { value: 'REJECTED', label: 'Rejected' },
];

const NoteLine = ({ label, text }) => (text ? (
  <Typography variant="body2" sx={{ mb: 0.5 }}>
    <Box component="span" sx={{ fontWeight: 600 }}>{label}: </Box>{text}
  </Typography>
) : null);

const ReturnsQueue = ({ onDecided }) => {
  const [status, setStatus] = useState('ESCALATED');
  const [page, setPage] = useState(0);
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deciding, setDeciding] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await storeReturnsApi.list({ statuses: [status], page, size: PAGE_SIZE });
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      setItems(null);
      setError(err.backendMessage || err.message || 'Failed to load returns');
    } finally {
      setLoading(false);
    }
  }, [status, page]);

  useEffect(() => { load(); }, [load]);

  const handleDecided = (result, decision) => {
    setDeciding(null);
    if (result) onDecided(result, decision);
    load();
  };

  const canDecide = status === 'ESCALATED';

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <Typography variant="body2" color="text.secondary">Show</Typography>
        <Select
          size="small"
          value={status}
          onChange={(e) => { setStatus(e.target.value); setPage(0); }}
          inputProps={{ 'aria-label': 'Return status filter' }}
        >
          {STATUS_FILTERS.map((f) => <MenuItem key={f.value} value={f.value}>{f.label}</MenuItem>)}
        </Select>
      </Box>

      {status === 'APPROVED' && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Approved returns are waiting for their refund to finish. If the refund outcome was unknown,
          the system retries it automatically; nothing to decide here.
        </Alert>
      )}

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress aria-label="Loading returns" />
        </Box>
      )}

      {!loading && error && (
        <Alert severity="error" action={<Button color="inherit" size="small" onClick={load}>Retry</Button>}>
          {error}
        </Alert>
      )}

      {!loading && !error && items && items.length === 0 && (
        <Alert severity="success">
          {canDecide ? 'No escalated returns waiting for a decision.'
            : `No ${RETURN_STATUS_LABELS[status].toLowerCase()} returns.`}
        </Alert>
      )}

      {!loading && !error && items && items.length > 0 && (
        <Paper>
          <TableContainer>
            <Table size="small" aria-label="Store returns">
              <TableHead>
                <TableRow>
                  <TableCell>Return</TableCell>
                  <TableCell>Order</TableCell>
                  <TableCell>Reason and notes</TableCell>
                  <TableCell align="right">Refundable</TableCell>
                  <TableCell>Escalated</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((item) => {
                  const r = item.returnRequest;
                  const o = item.order;
                  return (
                    <TableRow key={r.id} hover data-testid={`return-row-${r.id}`} sx={{ verticalAlign: 'top' }}>
                      <TableCell>
                        #{r.id}
                        <Box><Chip size="small" label={RETURN_STATUS_LABELS[r.status] || r.status} /></Box>
                      </TableCell>
                      <TableCell>
                        <Link component={RouterLink} to={`/orders/${o.orderId}`}>Order #{o.orderId}</Link>
                        <Typography variant="body2">{o.storeName || '—'} to {o.buyerName || '—'}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {(o.items || []).map((i) => `${i.quantity} x ${i.name}`).join(', ')}
                        </Typography>
                        <Typography variant="caption" display="block" color="text.secondary">
                          Total {formatMoney(o.total, o.currencyCode)}, refunded so far{' '}
                          {formatMoney(o.refundedAmount, o.currencyCode)}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ maxWidth: 360 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                          {RETURN_REASON_LABELS[r.reason] || r.reason}
                        </Typography>
                        <NoteLine label="Buyer note" text={r.buyerNote} />
                        <NoteLine label="Store note" text={r.storeNote} />
                        <NoteLine label="Buyer dispute" text={r.disputeNote} />
                        <NoteLine label="Admin note" text={r.adminNote} />
                      </TableCell>
                      <TableCell align="right">{formatMoney(o.refundableAmount, o.currencyCode)}</TableCell>
                      <TableCell>
                        {formatDateTime(r.escalatedAt)}
                        {r.escalationReason && (
                          <Typography variant="caption" display="block" color="text.secondary">
                            {ESCALATION_LABELS[r.escalationReason] || r.escalationReason}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {canDecide && (
                          <Button size="small" variant="contained" onClick={() => setDeciding(item)}>
                            Decide
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, p: 1 }}>
            <Button size="small" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>Previous</Button>
            <Button size="small" disabled={items.length < PAGE_SIZE} onClick={() => setPage((p) => p + 1)}>Next</Button>
          </Box>
        </Paper>
      )}

      {deciding && (
        <ReturnDecisionDialog
          key={deciding.returnRequest.id}
          item={deciding}
          onClose={() => setDeciding(null)}
          onDecided={handleDecided}
        />
      )}
    </>
  );
};

/** Admin page: escalated store returns to decide, and late shipments to chase. */
const StoreReturns = () => {
  const [tab, setTab] = useState('returns');
  const [success, setSuccess] = useState('');

  useEffect(() => { document.title = 'Store returns'; }, []);

  const handleDecided = (result, decision) => {
    const id = result?.returnRequest?.id;
    if (decision === 'approve') {
      const amt = formatMoney(result?.returnRequest?.refundAmount, result?.order?.currencyCode);
      setSuccess(`Return #${id} approved. ${amt} refunded to the buyer.`);
    } else {
      setSuccess(`Return #${id} rejected.`);
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Typography variant="h5" component="h1" gutterBottom>Store returns</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Returns the store did not answer in time, or the buyer disputed, wait here for an admin
        decision. Late shipments are paid orders the store has not shipped by the promised date.
      </Typography>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab value="returns" label="Returns" />
        <Tab value="late" label="Late shipments" />
      </Tabs>

      {tab === 'returns' ? <ReturnsQueue onDecided={handleDecided} /> : <LateShipmentsList />}

      <Snackbar
        open={!!success}
        autoHideDuration={8000}
        onClose={() => setSuccess('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setSuccess('')} sx={{ width: '100%' }}>{success}</Alert>
      </Snackbar>
    </Container>
  );
};

export default StoreReturns;
